import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    catchError,
    forkJoin,
    interval,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    take,
    tap,
    throwError
} from 'rxjs';
import { UserService } from 'app/services/user.service';
import {LoginResponse, OidcSecurityService} from 'angular-auth-oidc-client';
import {NICEApiHelper} from '../models/NICE_ApiHelper.model';
import {AACDataService} from './aac-data.service';
import {IcDataUserService} from './ic-data.user.service';
import {NICEBusinessUnit} from '../models/NICE_BusinessUnit.model';
import {AACUser} from '../models/AAC_User.model';
import {Router} from '@angular/router';
import {GeneralService} from "./general.service";


@Injectable()
export class AuthService
{
    public apiHelper: NICEApiHelper = new NICEApiHelper();
    public businessUnit: NICEBusinessUnit = new NICEBusinessUnit();
    public oidcConfigId= 'CXone-Standard';
    public tokenData: any;
    public studioToken = '';
    public isLoggedOut$: Subject<boolean>;
    public userPermisions: Map<string, string>;
    private _authenticated = false;
    private logoffWindow: any;
    public authAuthority: string;


    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _icUserService: IcDataUserService,
        private _aacData: AACDataService,
        private _router: Router,
        private _genHttp:GeneralService,
        public oidcSecurityService: OidcSecurityService,
    )
    {
        this.isLoggedOut$ = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    public get acdBaseURL(): string {
        return this.apiHelper.apiAcdUrl;
    }
    public get apiBaseURL(): string {
        return this.apiHelper.apiBaseUrl;
    }

    public get licenseURL(): string {
        return this.apiHelper.aacLicenseUrl;
    }
    public get awsURL(): string {
        return this.apiHelper.aacAwsUrl;
    }
    public get utilityURL(): string {
        return this.apiHelper.aacUtilityUrl;
    }
    public get skyDataURL(): string {
        return this.apiHelper.aacSkyDataUrl;
    }
    public get apiVersion(): string {
        return this.apiHelper.apiVersion;
    }
    public get cXoneAgentID(): string {
        if(this.tokenData.agentId){
            return this.tokenData.agentId;
        }
        return this.tokenData.icAgentId;
    }

    public getToken(): Observable<any> {
        const studioToken = sessionStorage['StudioAuth'];
        if (studioToken && studioToken !=='')
        {
            return of(studioToken);
        }
        return this.oidcSecurityService.getAccessToken(this.oidcConfigId);
    }

    setTokenData(token){
        const tokenJSONData = window.atob(token.split('.')[1]);
        this.tokenData = JSON.parse(tokenJSONData);
    }

    public forceRefreshToken(): Observable<LoginResponse>
    {
        const studioToken = sessionStorage['StudioAuth'];
        if (studioToken!=='') {
            return throwError(() => new Error('Cannot Refresh Studio Token'));
        }
        return this.oidcSecurityService.forceRefreshSession({},this.oidcConfigId).pipe(
            catchError(
                () => throwError(() => new Error('TokenRefreshFailure'))));
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._authenticated = false;
        this.oidcConfigId = 'CXone-Standard';
        this.isLoggedOut$.next(true);
        this.isLoggedOut$.complete();
        const studioToken = sessionStorage['StudioAuth'];
        if (studioToken && studioToken !=='')
        {
            this.clearStudioAuth();
            this._router.navigateByUrl('login').then();
        }
        else{
            const config$ = this.oidcSecurityService.getConfiguration(this.oidcConfigId);
            const idToken$ = this.oidcSecurityService.getIdToken(this.oidcConfigId);

            forkJoin([config$, idToken$]).subscribe({
                    next: ([configData,idToken])=> {
                        if(configData && configData.authWellknownEndpoints){
                            this.logoffWindow = window.open(
                                configData.authWellknownEndpoints.endSessionEndpoint + '?id_token_hint=' + idToken,
                                '_blank',
                                'location=yes,height=300,width=250,scrollbars=no,status=no'
                            );
                        }
                        interval(3000).pipe(take(1)).subscribe({
                                next: () => {
                                    this.logoffWindow.close();
                                }
                            }
                        );
                        this.oidcSecurityService.logoffLocal(this.oidcConfigId);
                        this._router.navigateByUrl('login').then();
                    }
                }
            );
        }
    }

    clearStudioAuth(): void
    {
        this.oidcConfigId = 'CXone-Standard';
        sessionStorage['StudioAuth'] = '';
    }
    discoverEndpoints() {
        return this.oidcSecurityService.getConfiguration(this.oidcConfigId).pipe(switchMap((configData) => {
            if(configData){
                this.authAuthority = configData.authority;
            }
            else {
                this.authAuthority = 'https://cxone.niceincontact.com';
            }
            const discoveryUrl = this.authAuthority + '/.well-known/cxone-configuration?tenantId=';
            const tenantId = this.tokenData.tenantId;
            const URL =`${discoveryUrl}${tenantId}`;
            return this._genHttp.icGet(URL).pipe(
                tap((disResponse)=>{
                    this.apiHelper.setAuthDataByDiscovery(disResponse,this.tokenData);
                    return {isAuthenticated: false, message: ''};
                }),
                catchError(err => {
                    console.log(err,err);
                    return of({isAuthenticated: false, message: 'Cannot Reach ' + URL}
                    )})
            );
        }))




    }

    checkAACAuth(): Observable<any>{
        return this.discoverEndpoints().pipe(
            switchMap(()=>{
                const aacBuValidation$ = this._aacData.validateRegistration(this.tokenData.icBUId, this.tokenData.name, this.tokenData.icAgentId);
                const niceBU$ = this._icUserService.getBUData(this.tokenData.icBUId);
                const niceUser$ = this._icUserService.getAgent(this.tokenData.icAgentId);
                const nicePermissions$ = this._icUserService.getPermissions(this.tokenData.icAgentId);
                return forkJoin([aacBuValidation$, niceBU$, niceUser$,nicePermissions$])
                    .pipe(map(([buResults, niceBU, niceUser,nicePermissions]) => {
                            this.businessUnit = niceBU;
                            this.userPermisions = nicePermissions.permissions;
                            if (buResults.success) {
                                if (buResults.aacBusinessUnit) {
                                    this.businessUnit.aacMaxAdminCount = buResults.aacBusinessUnit.maxActiveUsers;
                                }
                                if (buResults.registeredUsers) {
                                    for (const rUser of buResults.registeredUsers) {
                                        this.businessUnit.aacAdmins.push(rUser.username);
                                    }
                                }
                            }
                            // Skybox users are always authenticated
                            if (buResults.success
                                && buResults.registeredUser
                                && buResults.registeredUser.isSkyboxUser === 'Y') {
                                this._userService.user = new AACUser(buResults, niceUser.agent);

                                return {isAuthenticated: true, message: 'Authenticated'};
                            }
                            else if (buResults.success
                                && buResults.aacBusinessUnit
                                && buResults.aacBusinessUnit.isActive === 'Y'
                                && buResults.registeredUser
                                && buResults.registeredUser.userIsActive === 'Y') {
                                this._userService.user = new AACUser(buResults, niceUser.agent);
                                if(buResults.registeredUser.IsApproved === 'N'){
                                    return {isAuthenticated: false, message: 'User Not Approved'};
                                }
                                else if(buResults.registeredUser.IsVerified === 'N'){
                                    return {isAuthenticated: false, message: 'User Not Verified'};
                                }
                                return {isAuthenticated: true, message: 'Authenticated'};
                            }
                            else if (!buResults.success) {
                                this._userService.user = new AACUser(buResults, niceUser.agent);
                                return {isAuthenticated: false, message: buResults.error.error.message};
                            }
                            this._userService.user = new AACUser(buResults, niceUser.agent);
                            return {isAuthenticated: false, message: 'BU Not Registered'};
                        }),
                        catchError(error=> of({isError: true, error: error})));
            })
        );
    }

    public checkUser(): Observable<any> {
        const studioSessionToken = sessionStorage['StudioAuth'];
        if (studioSessionToken && studioSessionToken !=='')
        {
            this.setTokenData(studioSessionToken);
            this.studioToken= studioSessionToken;
            this.oidcConfigId = 'Studio';
            return this.checkAACAuth().pipe(tap((authRes) => {
                this._authenticated = authRes.isAuthenticated;
                if(!authRes.isAuthenticated && authRes.message !== 'BU Not Registered'){
                    this.clearStudioAuth();
                }
            }));
        }
        return this.oidcSecurityService.checkAuthMultiple().pipe(
            switchMap((loginResult) =>
            {
                let activeLogin= loginResult[0];
                for (const lr of loginResult)
                {
                    if (lr.isAuthenticated)
                    {
                        activeLogin = lr;
                        this.oidcConfigId =activeLogin.configId;
                        this.setTokenData(activeLogin.accessToken);
                        break;
                    }
                }
                if (activeLogin.isAuthenticated)
                {
                    this._authenticated =true;
                    return this.checkAACAuth();
                }
                else {
                    this.oidcSecurityService.logoffLocal(this.oidcConfigId);
                    return of({isAuthenticated: false, message: 'User not authenticated.'});
                }
            }));
    }
    isAuthenticated():Observable<boolean>{
        if (this._authenticated) {

            return of(this._authenticated)
        }
        else
            return this.checkUser().pipe(map((checkUserResult)=>
                {
                    this._authenticated = checkUserResult.isAuthenticated;
                    return this._authenticated;
                })
            );
    }

}
